"use client";
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

interface TextRevealCardProps {
    text: string;
    revealText: string;
    children?: React.ReactNode;
    className?: string;
}

export const TextRevealCard = ({
    text,
    revealText,
    children,
    className,
}: TextRevealCardProps) => {
    const [widthPercentage, setWidthPercentage] = useState(0);
    const cardRef = useRef<HTMLDivElement>(null);
    const [left, setLeft] = useState(0);
    const [localWidth, setLocalWidth] = useState(0);
    const [isMouseOver, setIsMouseOver] = useState(false);

    useEffect(() => {
        if (cardRef.current) {
            const { left, width: localWidth } =
                cardRef.current.getBoundingClientRect();
            setLeft(left);
            setLocalWidth(localWidth);
        }
    }, []);

    const mouseMoveHandler = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();

        const { clientX } = event;
        if (cardRef.current) {
            const relativeX = clientX - left;
            setWidthPercentage((relativeX / localWidth) * 100);
        }
    };

    const mouseLeaveHandler = () => {
        setIsMouseOver(false);
        setWidthPercentage(0);
    };

    const mouseEnterHandler = () => {
        setIsMouseOver(true);
    };

    const touchMoveHandler = (event: React.TouchEvent<HTMLDivElement>) => {
        event.preventDefault();
        const clientX = event.touches[0]?.clientX;
        if (cardRef.current) {
            const relativeX = clientX - left;
            setWidthPercentage((relativeX / localWidth) * 100);
        }
    };

    const rotateDeg = (widthPercentage - 50) * 0.1;

    return (
        <div
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            onMouseMove={mouseMoveHandler}
            onTouchStart={mouseEnterHandler}
            onTouchEnd={mouseLeaveHandler}
            onTouchMove={touchMoveHandler}
            ref={cardRef}
            className={`bg-transparent border-none w-full rounded-none p-0 relative overflow-hidden flex justify-center mt-[3vw] ${className || ""}`}
        >
            {children}

            <div className="h-auto relative flex items-center overflow-hidden" style={{ fontFamily: "Himagsikan", }}>
                <motion.div
                    style={{
                        width: "100%",
                    }}
                    animate={
                        isMouseOver
                            ? {
                                opacity: widthPercentage > 0 ? 1 : 0,
                                clipPath: `inset(0 ${100 - widthPercentage}% 0 0)`,
                            }
                            : {
                                clipPath: `inset(0 ${100 - widthPercentage}% 0 0)`,
                            }
                    }
                    transition={isMouseOver ? { duration: 0 } : { duration: 0.4 }}
                    className="absolute w-full z-20 will-change-transform"
                >
                    <p className="text-[17.5vw] px-[3vw] leading-[19.5vw] font-bold text-[#360152]">{revealText}</p>
                </motion.div>

                <motion.div
                    animate={{
                        left: `${widthPercentage}%`,
                        rotate: `${rotateDeg}deg`,
                        opacity: widthPercentage > 0 ? 1 : 0,
                    }}
                    transition={isMouseOver ? { duration: 0 } : { duration: 0.4 }}
                    className="h-full w-2 bg-gradient-to-b from-transparent via-[#2c2c2c] to-transparent absolute z-50 will-change-transform"
                ></motion.div>

                <div className="relative overflow-visible">
                    <p className="text-[17.5vw] px-[3vw] leading-[19.5vw] font-bold text-[#220b2e]">{text}</p>
                </div>
            </div>
        </div>
    );
};

interface TextRevealCardTitleProps {
    children: React.ReactNode;
    className?: string;
}

export const TextRevealCardTitle = ({
    children,
    className,
}: TextRevealCardTitleProps) => {
    return (
        <h2 className={`text-white text-lg mb-2 ${className || ""}`}>{children}</h2>
    );
};

interface TextRevealCardDescriptionProps {
    children: React.ReactNode;
    className?: string;
}

export const TextRevealCardDescription = ({
    children,
    className,
}: TextRevealCardDescriptionProps) => {
    return (
        <p className={`text-[#a9a9a9] text-sm ${className || ""}`}>
            {children}
        </p>
    );
};
